import { formatWithUserZone } from '../../../../../shared/utils';
import {
  DEFAULT_SYNC_STRATEGY,
  SYNC_STRATEGY_ORDER,
} from '../config';
import {
  isShopifyWizardStep6SessionActive,
} from './shopifyWizardSession';

export const SHOPIFY_CHANNEL_TYPE = 'shopify';

const LEGACY_SYNC_STRATEGY_MAP = {
  all: ['active', 'draft', 'unlisted', 'archived'],
  active: ['active'],
  draft_active: ['draft', 'active'],
};

export const getPendingShopifyChannel = (team) => {
  const channels = team?.salesChannels || [];
  const pendingList = channels.filter(
    (channel) => channel.type === SHOPIFY_CHANNEL_TYPE && channel.setupStatus === 'pending',
  );

  if (!pendingList.length) return null;

  return pendingList.reduce((latest, channel) => {
    if (!latest) return channel;
    const latestTs = new Date(latest.updatedAt || latest.createdAt || 0).getTime();
    const channelTs = new Date(channel.updatedAt || channel.createdAt || 0).getTime();
    return channelTs >= latestTs ? channel : latest;
  }, null);
};

export const getConnectedShopifyChannels = (team) => (
  (team?.salesChannels || []).filter(
    (channel) => channel.type === SHOPIFY_CHANNEL_TYPE && channel.store,
  )
);

export const getConnectedShopifyChannelById = (team, salesChannelId) => (
  getConnectedShopifyChannels(team).find(
    (channel) => String(channel._id) === String(salesChannelId),
  ) || null
);

export const isSalesChannelDisconnected = (channel) => Boolean(channel?.disconnected);

export const formatChannelLastSynced = (channel, userZone) => {
  const raw = channel?.lastPulledAt || channel?.updatedAt;
  const formatted = formatWithUserZone(raw, userZone, 'h:mm A, D MMMM, YYYY');
  return formatted || null;
};

export const hasConnectedShopifyStore = (team) => (getConnectedShopifyChannels(team).length >= 1);

export const hasActiveConnectedShopifyStore = (team) => (
  getConnectedShopifyChannels(team).some((channel) => !isSalesChannelDisconnected(channel))
);

export const normalizeSyncStrategyFromApi = (apiValue) => {
  if (!apiValue) return [...DEFAULT_SYNC_STRATEGY];

  if (Array.isArray(apiValue)) {
    return SYNC_STRATEGY_ORDER.filter((value) => apiValue.includes(value));
  }

  if (typeof apiValue === 'string') {
    if (LEGACY_SYNC_STRATEGY_MAP[apiValue]) {
      return [...LEGACY_SYNC_STRATEGY_MAP[apiValue]];
    }

    if (SYNC_STRATEGY_ORDER.includes(apiValue)) {
      return [apiValue];
    }
  }

  return [...DEFAULT_SYNC_STRATEGY];
};

export const mapSyncStrategyToApi = (selectedValues = []) => (
  SYNC_STRATEGY_ORDER.filter((value) => selectedValues.includes(value))
);

export const hasSyncStrategyConfigured = (apiValue) => {
  if (!apiValue) return false;

  if (Array.isArray(apiValue)) {
    return apiValue.length > 0;
  }

  if (typeof apiValue === 'string') {
    return apiValue.trim().length > 0;
  }

  return false;
};

export const formatSyncStrategyLabels = (selectedValues, getLabel) => (
  mapSyncStrategyToApi(selectedValues)
    .map((value) => getLabel(value))
    .filter(Boolean)
    .join(', ')
);

export const getWizardHydrationFromTeam = (team) => {
  const pending = getPendingShopifyChannel(team);
  const connectedCount = getConnectedShopifyChannels(team).length;

  if (!pending) {
    return {
      pending: null,
      pendingSalesChannelId: null,
      step: null,
      shopifyDisplayName: '',
      selectedSyncStrategy: [...DEFAULT_SYNC_STRATEGY],
      connectedCount,
    };
  }

  const resolvedStep = resolveWizardStep({ pending, connectedCount });
  const step = resolvedStep === 5 && isShopifyWizardStep6SessionActive() ? 6 : resolvedStep;

  return {
    pending,
    pendingSalesChannelId: pending._id,
    step,
    shopifyDisplayName: pending.name || '',
    selectedSyncStrategy: normalizeSyncStrategyFromApi(pending.syncStrategy),
    connectedCount,
  };
};

/** Wizard start step when user clicks Connect on Channels list. */
export const resolveConnectShopifyStartStep = (team, { isSecondStoreAttempt = false } = {}) => {
  if (isSecondStoreAttempt || hasConnectedShopifyStore(team)) {
    return 1;
  }

  const hydration = getWizardHydrationFromTeam(team);
  return hydration.pending ? hydration.step : 1;
};

export const getPrimaryConnectedShopifyChannel = (team) => (getConnectedShopifyChannels(team)[0] || null);

export const isSalesChannelConnected = (team, channelType) => (
  (team?.salesChannels || []).some(
    (channel) => channel.type === channelType && channel.store,
  )
);

export const findSalesChannelByContextId = (team, channelId) => (
  (team?.salesChannels || []).find(
    (channel) => channel.type === channelId
      || channel.name === channelId
      || String(channel._id) === String(channelId),
  ) || null
);

export const isChannelConnectionLost = (team, channelId, hasLive) => {
  if (!channelId || !hasLive) return false;
  const channel = findSalesChannelByContextId(team, channelId);
  return isSalesChannelDisconnected(channel);
};

export const isContextChannelDisconnected = (team, channelId) => {
  if (!channelId) return false;
  const channel = findSalesChannelByContextId(team, channelId);
  return isSalesChannelDisconnected(channel);
};

/** Shopify store profile from the first connected sales channel (`team.salesChannels[].store`). */
export const getTeamShopifyStore = (team) => (getPrimaryConnectedShopifyChannel(team)?.store || null);

export const mergeSalesChannelIntoTeam = (team, salesChannel) => {
  const channels = [...(team?.salesChannels || [])];
  const index = channels.findIndex((channel) => String(channel._id) === String(salesChannel._id));

  if (index >= 0) {
    channels[index] = { ...channels[index], ...salesChannel };
  } else {
    channels.push(salesChannel);
  }

  return { ...team, salesChannels: channels };
};

export const removePendingShopifyFromTeam = (team) => {
  const channels = (team?.salesChannels || []).filter(
    (channel) => !(channel.type === SHOPIFY_CHANNEL_TYPE && channel.setupStatus === 'pending'),
  );

  return { ...team, salesChannels: channels };
};

export const removeSalesChannelByIdFromTeam = (team, salesChannelId) => {
  const channels = (team?.salesChannels || []).filter(
    (channel) => String(channel._id) !== String(salesChannelId),
  );

  return { ...team, salesChannels: channels };
};

export const getPrimaryConnectedShopifyChannelId = (team) => (getConnectedShopifyChannels(team)[0]?._id || null);

/**
 * @param {{ pending: object|null, connectedCount: number }} params
 * @returns {number|null} Wizard step 1–5 from team state (step 6 via session flag in hydration)
 */
export const resolveWizardStep = ({ pending }) => {
  if (!pending) return null;

  const name = pending.name?.trim();
  if (!name) {
    return 1;
  }
  if (!hasSyncStrategyConfigured(pending.syncStrategy)) {
    return 2;
  }
  if (!pending.store) {
    return 3;
  }
  return 5;
};
